import { useEffect, useRef, useState } from "react";
import { stringTocamelCase } from "../_helpers/utils";
import { CALLOUT } from "../constants/editor";
import useButtonAnimations from "./useButtonAnimations";

const useWidgetAutoAnimator = ({
  rootSelector = ".preview-content.customScroll",
  threshold = 0.5,
  margin = "0px",
  contextWidgets = [],
  active,
  setSlideIndex,
  slideIndex,
  isAudioMuted,
} = {}) => {
  const activeButtonAnimationsRef = useRef([]);
  const currentAudioRef = useRef(null);
  const prevSlideIndexRef = useRef(null);
  const totalSlidesRef = useRef(0);
  const isAudioPlayingRef = useRef(false);

  const { createRipple, createPulse, createPreviewSpinner } = useButtonAnimations();

  const [replayTrigger, setReplayTrigger] = useState(0);

  const TEXT_ANIMATION_PRESETS = {
    flipInX: {
      animationName: "flipInX",
      charClass: "flip-in-x",
      duration: 0.8,
      ease: "ease-out",
    },
    flipInY: {
      animationName: "flipInY",
      charClass: "flip-letter-y",
      duration: 0.8,
      ease: "ease-out",

      beforeWrap(el) {
        if (!el.parentNode.classList.contains("flip-y-root")) {
          const wrapper = document.createElement("div");
          wrapper.className = "flip-y-root";
          el.parentNode.insertBefore(wrapper, el);
          wrapper.appendChild(el);
        }

        el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());

        return el;
      },

      afterRestore(el) {
        const root = el.parentNode;
        if (root && root.classList.contains("flip-y-root")) {
          root.replaceWith(...root.childNodes);
        }
      },
    },
    bounceIn: {
      animationName: "bounceIn",
      charClass: "bounce-in-letter",
      duration: 0.9,
    },
    rotateIn: {
      animationName: "rotateIn",
      charClass: "rotate-in-letter",
      duration: 0.8,
    },
    rollIn: {
      animationName: "rollIn",
      charClass: "roll-in-char",
      duration: 0.8,
    },
    flipOutX: {
      animationName: "flipOutX",
      charClass: "flip-out-char",
      duration: 0.8,
      ease: "ease-in",
    },
    flipOutY: {
      animationName: "flipOutY",
      charClass: "flip-letter-y",
      duration: 0.8,
      ease: "ease-in",

      beforeWrap(el) {
        if (!el.parentNode.classList.contains("flip-y-root")) {
          const wrapper = document.createElement("div");
          wrapper.className = "flip-y-root";
          el.parentNode.insertBefore(wrapper, el);
          wrapper.appendChild(el);
        }

        el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());

        return el;
      },

      afterRestore(el) {
        const root = el.parentNode;
        if (root && root.classList.contains("flip-y-root")) {
          root.replaceWith(...root.childNodes);
        }
      },
    },
    bounceOut: {
      animationName: "bounceOut",
      charClass: "bounce-out-letter",
      duration: 0.9,
      ease: "ease-in",
    },
    rotateOut: {
      animationName: "rotateOut",
      charClass: "rotate-out-letter",
      duration: 0.8,
      ease: "ease-in",
    },
    rollOut: {
      animationName: "rollOut",
      charClass: "roll-out-char",
      duration: 0.7,
      ease: "ease-in",
    },
  };

  const playerHelper = {
    animationSpeedToDurationMsObj: {
      Fast: 500,
      Medium: 1000,
      Slow: 2000,
    },
    getWidgetData: ({ widgetObj, pageDurationMs }) => {
      const { startTime = 0, endTime = pageDurationMs } = widgetObj?.duration ?? {};
      const startTimeMs = parseFloat(startTime) * 1000;
      const endTimeMs = parseFloat(endTime) * 1000;
      const stopTimeMs = endTimeMs > pageDurationMs ? pageDurationMs : endTimeMs;
      const widgetNode = document.getElementById(widgetObj.id);
      const assetType = widgetObj.data["data-asset-type"];
      const source = widgetObj.data["data-source"];

      return { startTimeMs, stopTimeMs, widgetNode, assetType, source };
    },

    handleAnimationPlay: ({ widgetId, widgetNode }) => {
      let widgetObj = contextWidgets?.find(w => w.id === widgetId);
      if (!widgetObj) return;
      const { startTimeMs, stopTimeMs, assetType, source } = playerHelper.getWidgetData({ widgetObj });

      if (startTimeMs > 0) {
        setTimeout(() => {
          widgetNode.style.visibility = "";
          playerHelper.animationPlay({
            widgetNode,
            widgetObj,
          });
        }, startTimeMs);
      } else {
        widgetNode.style.visibility = "";
        playerHelper.animationPlay({
          widgetNode,
          widgetObj,
        });
      }
    },

    animationPlay: ({ widgetNode, widgetObj }) => {
      const showEntryAnimation = widgetObj.data["data-entrance-animation"];
      const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
      const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
      const entryAnimationDurationMs =
        showEntryAnimation && playerHelper.animationSpeedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);

      if (!showEntryAnimation) return;

      let animationNode = document.createElement("div");
      animationNode.classList.add("animation-node");
      animationNode.style.height = "100%";
      animationNode.style.width = "100%";
      [...widgetNode.childNodes].forEach(child => animationNode.append(child));
      widgetNode.innerHTML = "";
      widgetNode.append(animationNode);

      // entry animation start
      if (showEntryAnimation) {
        const preset = TEXT_ANIMATION_PRESETS[entryAnimationClassName];

        // text charater animation
        if (preset) {
          const el = widgetNode.querySelector(".primary-text-widget");
          if (!el) return;

          // store original html
          const originalHTML = el.innerHTML;
          widgetNode.setAttribute("data-original-html", originalHTML);

          // ✅ apply beforeWrap if exists
          const context = preset.beforeWrap ? preset.beforeWrap(el) : el;

          // ✅ wrap text nodes
          const wrapTextNodes = node => {
            [...node.childNodes].forEach(child => {
              if (child.nodeType === 3 && child.textContent.trim().length > 0) {
                const fragment = document.createDocumentFragment();

                const words = child.textContent.split(" ");

                words.forEach((word, wordIndex) => {
                  const wordSpan = document.createElement("span");
                  wordSpan.style.display = "inline-block"; // 🔥 IMPORTANT
                  wordSpan.style.whiteSpace = "nowrap"; // 🔥 PREVENT BREAK

                  [...word].forEach(char => {
                    const charSpan = document.createElement("span");
                    charSpan.className = preset.charClass;
                    charSpan.innerHTML = char === " " ? "&nbsp;" : char;
                    wordSpan.appendChild(charSpan);
                    // animatedCount++;
                  });

                  fragment.appendChild(wordSpan);

                  // add space after word (except last)
                  if (wordIndex !== words.length - 1) {
                    fragment.appendChild(document.createTextNode(" "));
                  }
                });

                child.replaceWith(fragment);
              } else if (child.nodeType === 1) {
                wrapTextNodes(child);
              }
            });
          };

          wrapTextNodes(context);

          const chars = context.querySelectorAll(`.${preset.charClass}`);
          const charCount = chars.length;
          if (!charCount) return;

          // ✅ SAME logic as your main engine
          const maxTotalTime = entryAnimationDurationMs / 1000;
          const animDuration = Math.min(preset.duration, maxTotalTime);

          const perCharDelay = charCount > 1 ? Math.max((maxTotalTime - animDuration) / (charCount - 1), 0) : 0;

          // ✅ apply animation properly
          chars.forEach((span, i) => {
            span.classList.add("play-animation"); // IMPORTANT

            span.style.animation = `
        ${preset.animationName}
        ${animDuration}s
        ${i * perCharDelay}s
        both
        ${preset.ease || "ease-out"}
      `;
          });
        }
        // other widget animation
        else {
          animationNode.classList.add("play-animation");
          animationNode.classList.add(entryAnimationClassName);
          animationNode.style.animationDuration = `${entryAnimationDurationMs}ms`;
        }
      }
    },

    getDynamicThreshold: (widget, root, defaultThreshold) => {
      const rootHeight = root?.clientHeight || window.innerHeight;
      const widgetHeight = widget.offsetHeight;

      // Giant widgets → trigger on entry
      if (widgetHeight > rootHeight * 1.5) {
        return 0;
      }

      // Normal tall widgets
      if (widgetHeight > rootHeight) {
        const maxVisibleRatio = rootHeight / widgetHeight;

        return Math.max(0.02, Math.min(defaultThreshold, maxVisibleRatio));
      }

      return defaultThreshold;
    },

    handleButtonAnimation: page => {
      const buttons = page.querySelectorAll("[data-button-animation]");

      buttons.forEach(button => {
        const animationType = button.getAttribute("data-button-animation");

        if (!animationType) return;

        let animationInstance;

        if (animationType === "Ripple") {
          const el = button.querySelector(`.dhp-widget-inner`);

          el.style.overflow = "hidden";
          el.style.position = "relative"; // VERY IMPORTANT

          // loop ripple
          const interval = setInterval(() => {
            createRipple(el);
          }, 1200);

          animationInstance = { type: "interval", value: interval };
        }

        if (animationType === "Pulse") {
          const pulse = createPulse(button);
          animationInstance = { type: "element", value: pulse };
        }

        if (animationType === "Spinner") {
          const spinner = createPreviewSpinner(button);
          animationInstance = { type: "element", value: spinner };
        }

        if (animationInstance) {
          activeButtonAnimationsRef.current.push({
            button,
            animationInstance,
          });
        }
      });
    },

    stopButtonAnimations: () => {
      activeButtonAnimationsRef.current.forEach(({ animationInstance }) => {
        if (animationInstance.type === "interval") {
          clearInterval(animationInstance.value);
        }

        if (animationInstance.type === "element") {
          animationInstance.value?.remove();
        }
      });

      activeButtonAnimationsRef.current = [];
    },

    handleCalloutAnimation: ({ widgetId, widgetNode }) => {
      let hasAnimation = false;
      let hasAudio = false;

      let delayBeforeAnimation = 800; // delay before zoom starts
      const animationDuration = 900; // your transform duration
      const audioStartOffset = 300; // start audio DURING animation

      try {
        const config = JSON.parse(widgetNode.getAttribute("data-config"));
        hasAnimation = config?.animation === true;
        hasAudio = config?.enableVoiceOver === true;

        delayBeforeAnimation = config?.animationDelayMs ?? 800;
      } catch (e) {}

      const activePage = widgetNode.closest(".preview-page");
      if (!activePage) return;

      if (hasAnimation) {
        // 1️⃣ Start animation
        setTimeout(() => {
          playerHelper.focusCallout(activePage, widgetNode);
        }, delayBeforeAnimation);

        // 2️⃣ Start audio DURING animation
        if (hasAudio) {
          setTimeout(() => {
            playerHelper.playCalloutAudio(widgetNode, isAudioMuted);
          }, delayBeforeAnimation + audioStartOffset);
        }
      } else {
        playerHelper.resetPreview(activePage);

        // no animation → play immediately
        if (hasAudio) {
          playerHelper.playCalloutAudio(widgetNode, isAudioMuted);
        }
      }
    },

    playCalloutAudio: (widgetNode, isAudioMuted) => {
      let audioSrc = "";
      let calloutVoiceEnable;

      try {
        const config = JSON.parse(widgetNode.getAttribute("data-config"));
        audioSrc = config?.voiceOverAudioFile;
        calloutVoiceEnable = config?.enableVoiceOver;
      } catch (e) {}

      if (!audioSrc || !calloutVoiceEnable) return;

      // prevent duplicate calls
      if (isAudioPlayingRef.current) return;

      // stop previous
      playerHelper.stopAudio();

      if (isAudioMuted) return;

      const audio = new Audio(audioSrc);

      currentAudioRef.current = audio;
      isAudioPlayingRef.current = true; // 🔒 lock

      audio.play().catch(() => {});

      audio.onended = () => {
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
          isAudioPlayingRef.current = false; // 🔓 unlock
        }
      };
    },

    stopAudio: () => {
      const audio = currentAudioRef.current;

      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        currentAudioRef.current = null;
      }

      isAudioPlayingRef.current = false; // 🔓 VERY IMPORTANT
    },

    focusCallout: (page, callout) => {
      const previewLayer = page.querySelector('div[style*="transform-origin"]');
      if (!previewLayer) return;

      // RESET
      previewLayer.style.transition = "none";
      previewLayer.style.transform = "scale(1)";
      previewLayer.style.transformOrigin = "center center";

      previewLayer.getBoundingClientRect(); // force reflow

      const pageRect = page.getBoundingClientRect();
      const rect = callout.getBoundingClientRect();

      // get callout center RELATIVE to page
      const originX = ((rect.left - pageRect.left + rect.width / 2) / pageRect.width) * 100;
      const originY = ((rect.top - pageRect.top + rect.height / 2) / pageRect.height) * 100;

      // move "camera focus" instead of moving page
      previewLayer.style.transformOrigin = `${originX}% ${originY}%`;

      // very small zoom (Supademo style)
      const scale = 1.08;

      previewLayer.style.transition = "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)";

      previewLayer.style.transform = `scale(${scale})`;
    },

    resetPreview: page => {
      const previewLayer = page.querySelector('div[style*="transform-origin"]');

      if (!previewLayer) return;

      previewLayer.style.transition = "none";
      previewLayer.style.transform = "translate(0px, 0px) scale(1)";
    },

    goToPage: (currentPage, direction) => {
      const slides = document.querySelectorAll(".preview-page");
      const allPages = Array.from(slides);
      const currentIndex = allPages.indexOf(currentPage);

      let targetIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

      if (targetIndex < 0 || targetIndex >= allPages.length) return;

      // ONLY PAGE CHANGE
      slides.forEach(el => el.classList.add("d-none"));
      slides[targetIndex]?.classList.remove("d-none");

      setSlideIndex(targetIndex + 1);
    },
  };

  const setupIntersectionObserver = () => {
    if (!contextWidgets?.length) return;

    const root = document.querySelector(rootSelector);
    const animated = new Set();
    const observers = [];

    const observeWidgets = () => {
      const widgets = document.querySelectorAll("[data-id][data-class='dhp-root-widgets']");
      if (!widgets.length) {
        requestAnimationFrame(observeWidgets);
        return;
      }

      widgets.forEach(widget => {
        const widgetId = widget.getAttribute("data-id");
        const widgetObj = contextWidgets.find(w => w.id === widgetId);
        if (!widgetObj) return;

        const hasEntranceAnimation = widgetObj?.data?.["data-entrance-animation"];

        if (hasEntranceAnimation) {
          widget.style.visibility = "hidden";
        }

        const dynamicThreshold = playerHelper.getDynamicThreshold(widget, root, threshold);

        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (!entry.isIntersecting || animated.has(widgetId)) return;

              playerHelper.handleAnimationPlay({
                widgetId,
                widgetNode: widget,
              });

              animated.add(widgetId);
              observer.unobserve(widget);
            });
          },
          {
            root,
            rootMargin: margin,
            threshold: dynamicThreshold,
          }
        );

        observer.observe(widget);
        observers.push(observer);
      });
    };

    observeWidgets();

    return () => {
      observers.forEach(o => o.disconnect());
    };
  };

  const attachCalloutNavigation = () => {
    const callouts = document.querySelectorAll('[data-widget="CALLOUT"]');

    callouts.forEach(callout => {
      const page = callout.closest(".preview-page");
      if (!page) return;

      const leftArrow = callout.querySelector(".icon-ui-arrow-left2");
      const rightArrow = callout.querySelector(".icon-ui-arrow-right2");

      // PREVIOUS
      if (leftArrow && !leftArrow.classList.contains("disable")) {
        leftArrow.style.cursor = "pointer";

        leftArrow.onclick = e => {
          e.stopPropagation();
          playerHelper.goToPage(page, "prev");
        };
      }

      // NEXT
      if (rightArrow && !rightArrow.classList.contains("disable")) {
        rightArrow.style.cursor = "pointer";

        rightArrow.onclick = e => {
          e.stopPropagation();
          playerHelper.goToPage(page, "next");
        };
      }
    });
  };

  const waitForSlides = (callback, retries = 20) => {
    const slides = document.querySelectorAll(".preview-page");

    if (slides.length) {
      callback(slides);
    } else if (retries > 0) {
      setTimeout(() => waitForSlides(callback, retries - 1), 100);
    }
  };

  const getCurrentPageCallout = () => {
    const slides = document.querySelectorAll(".preview-page");
    const currentIndex = slideIndex - 1;

    const currentPage = slides[currentIndex];
    if (!currentPage) return null;

    return currentPage.querySelector('[data-widget="CALLOUT"]');
  };

  useEffect(() => {
    // when preview becomes inactive (closed)
    if (!active) {
      playerHelper.stopAudio();
      playerHelper.stopButtonAnimations();
    }
  }, [active]);

  useEffect(() => {
    if (isAudioMuted) {
      // stop immediately
      playerHelper.stopAudio();
    } else {
      // play ONLY current page callout
      const callout = getCurrentPageCallout();

      if (callout) {
        playerHelper.playCalloutAudio(callout, false);
      }
    }
  }, [isAudioMuted]);

  useEffect(() => {
    if (!active) return;
    const cleanup = setupIntersectionObserver();
    // ⏳ wait for DOM render
    setTimeout(() => {
      attachCalloutNavigation();
    }, 300);

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [contextWidgets, active]);

  useEffect(() => {
    if (!active) return;

    waitForSlides(slides => {
      totalSlidesRef.current = slides.length; // store total slides
      const prevIndex = prevSlideIndexRef.current;
      const currentIndex = slideIndex - 1;

      const currentPage = slides[currentIndex];
      const prevPage = slides[prevIndex];

      // STOP AUDIO
      playerHelper.stopAudio();

      // STOP BUTTON ANIMATION (ADD THIS)
      playerHelper.stopButtonAnimations();

      // RESET PREVIOUS PAGE
      if (prevPage) {
        playerHelper.resetPreview(prevPage);
      }

      // RUN CURRENT PAGE CALLOUT and BUTTON
      if (currentPage) {
        const callout = currentPage.querySelector('[data-widget="CALLOUT"]');

        // CALLOUT
        if (callout) {
          const widgetId = callout.getAttribute("data-id");

          setTimeout(() => {
            playerHelper.handleCalloutAnimation({
              widgetId,
              widgetNode: callout,
            });
          }, 300);
        }

        //BUTTON
        if (currentPage.querySelector('[data-widget="BUTTON"]')) {
          setTimeout(() => {
            playerHelper.handleButtonAnimation(currentPage);
          }, 600);
        }
      }

      // SHOW replay icon on last slide
      const replayIcon = document.querySelector(".replay-icon");
      if (replayIcon) {
        if (slideIndex === totalSlidesRef.current) {
          // SHOW on last slide
          replayIcon.classList.remove("d-none");
        } else {
          // HIDE on other slides
          replayIcon.classList.add("d-none");
        }
      }

      prevSlideIndexRef.current = currentIndex;
    });
  }, [slideIndex, active, replayTrigger]);

  useEffect(() => {
    const handleReplayClick = e => {
      const replayIcon = e.target.closest(".replay-icon");
      if (!replayIcon) return;

      e.stopPropagation();

      playerHelper.stopAudio();
      playerHelper.stopButtonAnimations();

      const slides = document.querySelectorAll(".preview-page");

      slides.forEach(el => {
        el.classList.add("d-none");
        playerHelper.resetPreview(el);
      });

      slides[0]?.classList.remove("d-none");

      prevSlideIndexRef.current = null;

      if (slides.length === 1) {
        // trigger effect manually
        setReplayTrigger(prev => prev + 1);
      } else {
        setSlideIndex(1);
      }
    };

    document.addEventListener("click", handleReplayClick);

    return () => {
      // cleanup when component unmounts
      document.removeEventListener("click", handleReplayClick);
      playerHelper.stopAudio();
      playerHelper.stopButtonAnimations();
    };
  }, []);
};

export default useWidgetAutoAnimator;
